'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';

import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { useListingActionContext } from '@/src/components/features/listing-actions';
import {
  contactSubmitLabelKey,
  contactTitleKey,
} from '@/src/components/features/property/ContactForm/contactSubmitLabel';
import { getTourOptions, TOUR_TYPE_LABELS } from '@/src/components/features/property/TourForm/tourUtils';
import { useHostedTourAction } from '@/src/components/features/property/TourForm';
import { useListingCTAAvailability } from '@/src/components/features/property/hooks/useListingCTAAvailability';
import { useCurrentUser } from '@/src/lib/hooks/query';
import { identityLoadingResult, textResult, useWebMcpTools } from '@/src/lib/webmcp';
import type { WebMcpTool } from '@/src/lib/webmcp';

/**
 * The HDP's mutating WebMCP tools: save this listing, message its property manager, book a tour.
 *
 * The two outward-facing tools are prepare-and-confirm: they open the real form, pre-filled, and
 * send nothing. Why that is not timidity, and why `save_listing` needs no such gate, is in
 * docs/HP-7461/webmcp-actions.md.
 *
 * Mounted inside `TourActionHost`, whose wizard `book_tour` opens. Renders nothing.
 */
export function WebMcpListingTools({ listing }: { listing?: PropertyDetailListing | null }) {
  const t = useTranslations('contactForm');
  const { data: currentUser } = useCurrentUser();
  const { openContact, isListingSaved, toggleSaveListing } = useListingActionContext();
  const { openTour, isTourReady } = useHostedTourAction('webmcp_tool', listing);
  // The same gate the page's own CTAs use, so an agent is offered exactly what a renter is: it
  // also covers an off-market listing, a waitlist already joined, and a tour already scheduled.
  const availability = useListingCTAAvailability(listing);

  const listingName = listing?.displayName || listing?.address?.street || 'this listing';
  // Read the send button's real label rather than naming it: it becomes "Add me to the waitlist"
  // on a waitlisted listing, and it is translated on the /es routes.
  const contactSubmitLabel = t(contactSubmitLabelKey('contact', listing));
  const tourTypeLabels = getTourOptions(listing?.details?.instantTourProviders)
    .map((option) => TOUR_TYPE_LABELS[option.type].toLowerCase())
    .join(', ');

  const tools = useMemo<Array<WebMcpTool>>(
    () => [
      {
        name: 'save_listing',
        description:
          "Save the rental listing on this page to the renter's HotPads favorites. Saving needs a HotPads account: if nobody is signed in, this opens a sign-in prompt and the listing is saved once they sign in.",
        inputSchema: { type: 'object', properties: {}, required: [] },
        execute: async () => {
          if (!listing) return textResult('There is no listing on this page to save.');
          if (currentUser === undefined) return identityLoadingResult();
          if (isListingSaved(listing)) return textResult(`${listingName} is already in the renter's favorites.`);

          const accepted = await toggleSaveListing(listing, {
            triggerLocation: 'webmcp_tool',
            triggerObject: 'home_details_component|action_bar',
          });
          if (!accepted) return textResult(`Could not save ${listingName}. Call this tool again in a moment.`);

          return currentUser?.loggedIn
            ? textResult(`Saved ${listingName} to the renter's HotPads favorites.`)
            : textResult(
                `${listingName} is not saved yet, because saving needs a HotPads account. A sign-in prompt is now open. The listing is saved automatically after the renter signs in.`,
              );
        },
      },
      {
        name: 'message_property_manager',
        description:
          "Open a message to this listing's property manager with your text already filled in. This does NOT send the message: the contact form opens on screen, and the renter reads it and presses Send. Use this to write an inquiry for the renter, then tell them to check it and send it. If the renter is not signed in, sending also creates a HotPads account for them.",
        inputSchema: {
          type: 'object',
          properties: {
            message: {
              type: 'string',
              description:
                "The message to put in the form, written in the renter's own voice. Ask the questions the renter wants an answer to, such as availability, parking, or the pet policy.",
            },
          },
          required: ['message'],
        },
        execute: async (args) => {
          const { message } = args;
          if (typeof message !== 'string' || message.trim() === '') {
            return textResult('A message is required. Write what the renter wants to ask the property manager.');
          }
          if (!listing) return textResult('There is no listing on this page to ask about.');
          if (currentUser === undefined) return identityLoadingResult();
          if (!availability.contact) {
            return textResult(
              `${listingName} is not taking messages: it is either off the market or a waitlist the renter has already joined. The page offers no contact form to open.`,
            );
          }

          // Awaited: openContact yields a frame for INP before it mounts the modal, so firing it
          // bare would report the form as open before it was. The guard is for the agent
          // boundary, not for a known throw - every path here owes the agent a result it can
          // read, and an exception would surface as an opaque tool error instead.
          try {
            await openContact(listing, {
              initialMessage: message.trim(),
              title: t(contactTitleKey(listing)),
              triggerLocation: 'webmcp_tool',
              triggerObject: 'home_details_component|summary',
            });
          } catch {
            return textResult(`Could not open the contact form for ${listingName}. Call this tool again in a moment.`);
          }

          return textResult(
            `The contact form for ${listingName} is open with your message in it. Nothing has been sent. Tell the renter to check the message and press "${contactSubmitLabel}" themselves.`,
          );
        },
      },
      {
        name: 'book_tour',
        description:
          "Open the tour booking wizard for this listing, which shows the property's real available dates and times. This does NOT book a tour: the renter picks a time and confirms it. Use this when the renter wants to see the property in person or on video. If the renter is not signed in, confirming the tour also creates a HotPads account for them.",
        inputSchema: { type: 'object', properties: {}, required: [] },
        execute: async () => {
          if (!listing) return textResult('There is no listing on this page to tour.');
          if (!isTourReady || !availability.toursReady) {
            return textResult('Tour availability is still loading. Call this tool again in a moment.');
          }
          if (availability.hasScheduledTour) {
            return textResult(
              `The renter already has a tour scheduled for ${listingName}. Booking another here would be a second booking - they change or cancel the existing one from the tour banner on this page.`,
            );
          }
          if (!availability.tour) {
            return textResult(
              `${listingName} does not offer tour booking on HotPads. Use message_property_manager to ask the property manager about a tour instead.`,
            );
          }

          await openTour();

          return textResult(
            `The tour booking wizard for ${listingName} is open, and it offers: ${tourTypeLabels}. Nothing is booked. Tell the renter to pick a date and time and confirm it themselves.`,
          );
        },
      },
    ],
    [
      availability.contact,
      availability.hasScheduledTour,
      availability.tour,
      availability.toursReady,
      contactSubmitLabel,
      currentUser,
      isListingSaved,
      isTourReady,
      listing,
      listingName,
      openContact,
      openTour,
      t,
      toggleSaveListing,
      tourTypeLabels,
    ],
  );

  useWebMcpTools(tools);

  return null;
}
